import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { ModuleRegistry } from "ag-grid-community";
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { getRawData } from '../../services/front_api';
import { getColdef } from '../../services/admin_api';
import { useTheme } from '../../context/ThemeContext';

const formatNumberWithThousandsSeparator = (value) => {
    if (value === null || value === undefined || value === '') {
        return value;
    }

    const rawValue = typeof value === 'number'
        ? String(value)
        : (typeof value === 'string' ? value.trim() : null);

    if (!rawValue || !/^-?\d+(?:[.,]\d+)?$/.test(rawValue)) {
        return value;
    }

    const decimalSeparator = rawValue.includes(',') ? ',' : (rawValue.includes('.') ? '.' : '');
    const [signedIntegerPart, decimalPart] = decimalSeparator ? rawValue.split(decimalSeparator) : [rawValue];
    const sign = signedIntegerPart.startsWith('-') ? '-' : '';
    const integerPart = sign ? signedIntegerPart.slice(1) : signedIntegerPart;
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    if (decimalPart === undefined) {
        return `${sign}${formattedIntegerPart}`;
    }

    return `${sign}${formattedIntegerPart}${decimalSeparator}${decimalPart}`;
};

const formatGridNumericValue = (params) => formatNumberWithThousandsSeparator(params.value);

const PIVOT_FIELD_SEPARATOR = '||';
const LEGACY_PIVOT_FIELD_SEPARATOR = '_';

const isPresent = (value) => value !== undefined && value !== null && value !== '';

const getUniqueStringValues = (values) => Array.from(new Set(
    values
        .filter(isPresent)
        .map((value) => String(value))
        .filter((value) => value !== '')
));

const stripAggregationPrefix = (value) => {
    if (!isPresent(value)) {
        return value;
    }

    const label = String(value).trim();
    const match = label.match(/^(sum|avg|min|max|count|first|last)\s*\((.*)\)$/i);

    return match ? match[2] : label;
};

const getPivotValueColumnHeaderName = (pivotResultColDef) => {
    const pivotValueColumn = pivotResultColDef?.pivotValueColumn;
    const userColDef = pivotValueColumn?.getUserProvidedColDef?.();
    const sourceColDef = pivotValueColumn?.getColDef?.();

    return getUniqueStringValues([
        userColDef?.headerName,
        sourceColDef?.headerName,
        sourceColDef?.field,
        pivotValueColumn?.getColId?.(),
        pivotResultColDef?.headerName,
    ].map(stripAggregationPrefix))[0];
};

const getValueColumnId = (valueColumn) => getUniqueStringValues([
    valueColumn?.id,
    valueColumn?.colId,
    valueColumn?.field,
])[0];

const getValueColumnSuffixCandidates = (valueColumn) => getUniqueStringValues([
    valueColumn?.id,
    valueColumn?.colId,
    valueColumn?.field,
]).sort((a, b) => b.length - a.length);

const splitPivotPath = (pivotPath) => {
    if (!isPresent(pivotPath)) {
        return [];
    }

    const normalizedPath = String(pivotPath);
    const separator = normalizedPath.includes(PIVOT_FIELD_SEPARATOR)
        ? PIVOT_FIELD_SEPARATOR
        : LEGACY_PIVOT_FIELD_SEPARATOR;

    return normalizedPath
        .split(separator)
        .filter((part) => part !== '');
};

const findValueColumnSuffix = (field, valueColumns) => {
    const fieldName = String(field);

    for (const valueColumn of valueColumns || []) {
        const valueColumnId = getValueColumnId(valueColumn);

        if (!valueColumnId) {
            continue;
        }

        for (const candidate of getValueColumnSuffixCandidates(valueColumn)) {
            for (const separator of [PIVOT_FIELD_SEPARATOR, LEGACY_PIVOT_FIELD_SEPARATOR]) {
                const suffix = `${separator}${candidate}`;

                if (fieldName.endsWith(suffix)) {
                    return {
                        pivotPath: fieldName.slice(0, -suffix.length),
                        valueColumnId
                    };
                }
            }
        }
    }

    return null;
};

const normalizePivotResultField = (field, valueColumns) => {
    const fieldName = String(field);
    const valueColumnSuffix = findValueColumnSuffix(fieldName, valueColumns);

    if (valueColumnSuffix) {
        return [
            ...splitPivotPath(valueColumnSuffix.pivotPath),
            valueColumnSuffix.valueColumnId
        ].join(PIVOT_FIELD_SEPARATOR);
    }

    return splitPivotPath(fieldName).join(PIVOT_FIELD_SEPARATOR);
};

const normalizePivotResponseFields = (pivotCols, rows, valueColumns) => {
    if (!Array.isArray(pivotCols) || pivotCols.length === 0) {
        return { pivotCols, rows };
    }

    const fieldMap = new Map();
    const normalizedPivotCols = pivotCols.map((field) => {
        const originalField = String(field);
        const normalizedField = normalizePivotResultField(originalField, valueColumns);
        fieldMap.set(originalField, normalizedField);
        return normalizedField;
    });

    if (!Array.isArray(rows) || rows.length === 0) {
        return { pivotCols: normalizedPivotCols, rows };
    }

    const normalizedRows = rows.map((row) => {
        if (!row || typeof row !== 'object') {
            return row;
        }

        const normalizedRow = { ...row };

        fieldMap.forEach((normalizedField, originalField) => {
            if (normalizedField !== originalField && Object.prototype.hasOwnProperty.call(row, originalField)) {
                normalizedRow[normalizedField] = row[originalField];
            }
        });

        return normalizedRow;
    });

    return { pivotCols: normalizedPivotCols, rows: normalizedRows };
};

const shouldAddPivotGroupTotals = (valueColumns) => !Array.isArray(valueColumns) || valueColumns.length <= 1;

const FRENCH_MONTHS = [
    'Janvier',
    'Fevrier',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Aout',
    'Septembre',
    'Octobre',
    'Novembre',
    'Decembre',
];

const toMonthYearLabel = (value) => {
    if (value === null || value === undefined || value === '') {
        return value;
    }

    const rawValue = String(value).trim();

    const patterns = [
        /^([12]\d{3})[-/](0?[1-9]|1[0-2])$/, // YYYY-MM ou YYYY/M
        /^(0?[1-9]|1[0-2])[-/]([12]\d{3})$/, // MM-YYYY ou M/YYYY
        /^([12]\d{3})(0[1-9]|1[0-2])$/, // YYYYMM
        /^([12]\d{3})[-/](0[1-9]|1[0-2])[-/]\d{1,2}$/, // YYYY-MM-DD
    ];

    let year;
    let month;

    if (patterns[0].test(rawValue)) {
        const match = rawValue.match(patterns[0]);
        year = Number(match[1]);
        month = Number(match[2]);
    } else if (patterns[1].test(rawValue)) {
        const match = rawValue.match(patterns[1]);
        month = Number(match[1]);
        year = Number(match[2]);
    } else if (patterns[2].test(rawValue)) {
        const match = rawValue.match(patterns[2]);
        year = Number(match[1]);
        month = Number(match[2]);
    } else if (patterns[3].test(rawValue)) {
        const match = rawValue.match(patterns[3]);
        year = Number(match[1]);
        month = Number(match[2]);
    } else {
        return value;
    }

    if (!year || !month || month < 1 || month > 12) {
        return value;
    }

    return `${FRENCH_MONTHS[month - 1]} ${year}`;
};

const formatGridMonthValue = (params) => toMonthYearLabel(params.value);

const isMonthColumn = (columnDefinition) => {
    const headerName = typeof columnDefinition?.headerName === 'string'
        ? columnDefinition.headerName.trim().toLowerCase()
        : '';

    return headerName === 'mois';
};

const applyGridValueFormatting = (columnDefinitions) => columnDefinitions.map((columnDefinition) => {
    if (!columnDefinition || typeof columnDefinition !== 'object') {
        return columnDefinition;
    }

    if (columnDefinition.valueFormatter) {
        return columnDefinition;
    }

    if (isMonthColumn(columnDefinition)) {
        return {
            ...columnDefinition,
            valueFormatter: formatGridMonthValue,
        };
    }

    if (columnDefinition.aggFunc || columnDefinition.field === 'value') {
        return {
            ...columnDefinition,
            valueFormatter: formatGridNumericValue,
        };
    }

    return columnDefinition;
});

const DEFAULT_COL_DEFS = applyGridValueFormatting([
    { headerName: 'Categorie', colId: 'flt.label', field: 'ssrm_cat_visiteurs', rowGroup: true },
    { headerName: 'Nationalite', colId: "CASE WHEN src.foreigner = true THEN 'Etranger' ELSE 'Français' END", field: 'ssrm_nationalite', rowGroup: true },
    { headerName: 'Type', colId: 'src.type', field: 'ssrm_type', rowGroup: true },
    { headerName: 'Provenance', colId: 'prov.label', field: 'ssrm_provenance', rowGroup: true },
    { headerName: 'Date', colId: "TO_CHAR(src.record_date, 'YYYY-MM_DD')", field: 'ssrm_date', pivot: true },
    { headerName: 'Valeur', colId: 'src.volume', field: 'value', aggFunc: 'sum' },
]);

const normalizeColDefs = (content) => {
    if (Array.isArray(content)) {
        return applyGridValueFormatting(content);
    }

    if (typeof content === 'string') {
        try {
            const parsedContent = JSON.parse(content);
            return Array.isArray(parsedContent) ? applyGridValueFormatting(parsedContent) : null;
        } catch {
            return null;
        }
    }

    return null;
};

/**
 * En mode SSRM, AG Grid ne génère PAS automatiquement les colonnes
 * de total pour les groupes pivot expandables (addExpandablePivotGroups
 * n'est jamais appelé côté SSRM).
 *
 * Cette fonction :
 * 1. Regroupe les pivotResultFields par leur premier segment (mois/année)
 * 2. Insère un champ synthétique "Total" dans chaque groupe
 * 3. Calcule la somme des valeurs enfants pour ce champ dans chaque ligne
 *
 * Ainsi le groupe mois peut se replier et afficher la colonne Total,
 * puis se déplier pour révéler les jours.
 */
const addPivotGroupTotals = (pivotCols, rows, separator = PIVOT_FIELD_SEPARATOR, enabled = true) => {
    if (!enabled || !pivotCols || !Array.isArray(pivotCols) || pivotCols.length === 0) {
        return { pivotCols, rows };
    }

    // Grouper les champs pivot par leur premier segment (ex: "2024-01")
    const groups = new Map();
    const ungrouped = [];

    for (const field of pivotCols) {
        const fieldStr = String(field);
        const sepIdx = fieldStr.indexOf(separator);
        if (sepIdx > -1) {
            const groupKey = fieldStr.substring(0, sepIdx);
            if (!groups.has(groupKey)) {
                groups.set(groupKey, []);
            }
            groups.get(groupKey).push(fieldStr);
        } else {
            ungrouped.push(fieldStr);
        }
    }

    // Reconstruire la liste avec un champ "Total" inséré en tête de chaque groupe
    const newPivotCols = [];
    const totalFieldMap = new Map(); // groupKey -> { totalField, children }

    for (const [groupKey, children] of groups) {
        const totalField = `${groupKey}${separator}Total`;
        totalFieldMap.set(groupKey, { totalField, children });
        newPivotCols.push(totalField); // Total en premier dans le groupe
        for (const child of children) {
            newPivotCols.push(child);
        }
    }
    // Champs sans groupe (segment unique) à la fin
    newPivotCols.push(...ungrouped);

    // Calculer les totaux dans les données de chaque ligne
    if (rows && Array.isArray(rows)) {
        for (const row of rows) {
            for (const { totalField, children } of totalFieldMap.values()) {
                let total = 0;
                for (const childField of children) {
                    const val = row[childField];
                    if (val != null) {
                        total += Number(val) || 0;
                    }
                }
                row[totalField] = total;
            }
        }
    }

    console.log('[addPivotGroupTotals] Original pivotCols:', pivotCols);
    console.log('[addPivotGroupTotals] Transformed pivotCols:', newPivotCols);
    if (rows?.length > 0) {
        console.log('[addPivotGroupTotals] Sample row keys:', Object.keys(rows[0]));
    }

    return { pivotCols: newPivotCols, rows };
};

const createDatasource = (server, pivotGroupTotalsEnabledRef) => {
    return {
        // called by the grid when more rows are required
        getRows: params => {
            const ssrm = {
                "ssrm": params.request
            }
            console.log("[Datasource] - rows requested by grid: ", ssrm);
            // get data for request from server
            server.getData(ssrm).then(response => {
                console.log(response);
                if (response.success) {
                    const shouldAddTotals = shouldAddPivotGroupTotals(params.request?.valueCols);
                    pivotGroupTotalsEnabledRef.current = shouldAddTotals;
                    const normalizedResponse = normalizePivotResponseFields(
                        response.pivotCols,
                        response.rows,
                        params.request?.valueCols
                    );
                    // Ajouter les colonnes Total synthétiques par groupe mois/année
                    const { pivotCols, rows } = addPivotGroupTotals(
                        normalizedResponse.pivotCols,
                        normalizedResponse.rows,
                        PIVOT_FIELD_SEPARATOR,
                        shouldAddTotals
                    );
                    // supply rows for requested block to grid
                    params.success({
                        rowData: rows,
                        pivotResultFields: pivotCols
                    });
                } else {
                    // inform grid request failed
                    params.fail();
                }
            });
        }
    };
}

function createServer(app, query, debugUpdater) {
    return {
        getData: async (request) => {
            // in this simplified fake server all rows are contained in an array
            const [requestedRows, url, body] = await getRawData(app, query, 'grid', request);
            debugUpdater(requestedRows, url, body);
            return {
                success: true,
                rows: requestedRows.data,
                pivotCols: requestedRows.pivotFields
            };
        },
    };
}


const SSRMGrid = ({ app, query, queryId, debugUpdater }) => {
    const chartRef = useRef(null);
    const pivotGroupTotalsEnabledRef = useRef(true);
    const { themeName } = useTheme();

    ModuleRegistry.registerModules([AllEnterpriseModule]);
    LicenseManager.setLicenseKey("CompanyName=BlackBee,LicensedApplication=nVisio,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=0,AssetReference=AG-013902,ExpiryDate=25_March_2022_[v2]_MTY0ODE2NjQwMDAwMA==88d19d92c49d3c8107233f1da288adaf");

    // Column Definitions: Defines the columns to be displayed.
    const [colDefs, setColDefs] = useState(DEFAULT_COL_DEFS);
    const [loading, setLoading] = useState(true);

    const onGridReady = useCallback((params) => {
        // setup the fake server with entire dataset
        const dataServer = createServer(app, query, debugUpdater);
        const datasource = createDatasource(dataServer, pivotGroupTotalsEnabledRef);
        // register the datasource with the grid
        params.api.setGridOption("serverSideDatasource", datasource);
    }, [app, query, debugUpdater]);

    const defaultColDef = useMemo(() => {
        return {
            width: 140,
            minWidth: 56,
            sortable: true,
            resizable: true,
            cellStyle: {
                fontSize: '11px'
            }
        };
    }, []);

    const autoGroupColumnDef = useMemo(() => {
        return {
            width: 160,
            minWidth: 140,
            cellStyle: {
                fontSize: '11px'
            }
        };
    }, []);

    const processPivotResultColDef = useCallback((colDef) => {
        // console.log('[processPivotResultColDef]', {
        //     headerName: colDef.headerName,
        //     field: colDef.field,
        //     colId: colDef.colId,
        //     columnGroupShow: colDef.columnGroupShow,
        // });

        // La colonne "Total" (ajoutée par addPivotGroupTotals)
        // doit être visible quand le groupe parent est REPLIÉ.
        // Les autres colonnes (jours) ne sont visibles que quand il est DÉPLIÉ.
        const headerName = String(colDef.headerName || '');
        const formattedHeaderName = toMonthYearLabel(headerName);
        const valueColumnHeaderName = getPivotValueColumnHeaderName(colDef);

        // Si la clé pivot représente un mois calculé (ex: 2025-01),
        // on conserve son libellé formaté plutôt que de l'écraser.
        if (formattedHeaderName !== headerName) {
            colDef.headerName = formattedHeaderName;
        } else if (headerName !== 'Total' && valueColumnHeaderName) {
            colDef.headerName = valueColumnHeaderName;
        }

        colDef.valueFormatter = formatGridNumericValue;

        if (String(colDef.headerName || '') === 'Total') {
            colDef.columnGroupShow = 'closed';
        } else if (pivotGroupTotalsEnabledRef.current) {
            colDef.columnGroupShow = 'open';
        } else {
            delete colDef.columnGroupShow;
        }
    }, []);

    const processPivotResultColGroupDef = useCallback((colGroupDef) => {
        // console.log('[processPivotResultColGroupDef]', {
        //     headerName: colGroupDef.headerName,
        //     groupId: colGroupDef.groupId,
        // });
        colGroupDef.headerName = toMonthYearLabel(colGroupDef.headerName);
        colGroupDef.openByDefault = false;
    }, []);

    useEffect(() => {
        const fetchColdef = async () => {
            if (!queryId) {
                setLoading(false);
                return;
            }

            try {
                const data = await getColdef(queryId);
                const resolvedColDefs = normalizeColDefs(data?.content);

                if (resolvedColDefs && resolvedColDefs.length > 0) {
                    setColDefs(resolvedColDefs);
                } else {
                    setColDefs(DEFAULT_COL_DEFS);
                }

                console.log('ColDefs récupérées :', data.content);
            } catch (err) {
                console.error('Erreur lors de la récupération de la coldef', err);
                setColDefs(DEFAULT_COL_DEFS);
            } finally {
                setLoading(false);
            }
        };
        fetchColdef();
    }, [queryId]);

    if (loading) return <div>Chargement...</div>;

    const agThemeClass = themeName === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine';

    return (
        <div className={agThemeClass} style={{ height: 500, width: '100%' }}>
            <AgGridReact
                theme={'legacy'}
                columnDefs={colDefs}
                rowModelType={'serverSide'}
                rowHeight={32}
                headerHeight={34}
                pivotMode={true}
                suppressAggFuncInHeader={true}
                pivotDefaultExpanded={0}
                onGridReady={onGridReady}
                groupDisplayType={'multipleColumns'}
                groupIncludeFooter={false}
                suppressExpandablePivotGroups={false}
                serverSidePivotResultFieldSeparator={PIVOT_FIELD_SEPARATOR}
                processPivotResultColDef={processPivotResultColDef}
                processPivotResultColGroupDef={processPivotResultColGroupDef}
                defaultColDef={defaultColDef}
                autoGroupColumnDef={autoGroupColumnDef}
                animateRows={true}
                debug
            />
        </div>

    )
}

export default SSRMGrid;
